import React from "react";

const faqData = [
  {
    question: "1. What is USACarBooking?",
    answer:
      "USACarBooking is an independent car rental enquiry assistance website. We help customers submit rental requests for USA city and airport locations. We are not affiliated with, endorsed by, or sponsored by any car rental brand, rental supplier, or rental company.",
  },
  {
    question: "2. Do you own or operate rental cars?",
    answer:
      "No. USACarBooking does not own, operate, manage, or directly supply rental vehicles. Our service is independent. Final vehicle availability, pricing, itinerary details, and rental terms are determined by the applicable rental provider.",
  },
  {
    question: "3. How does the car rental enquiry process work?",
    answer:
      "You can submit a simple request by entering your dates, times, locations, and preferred vehicle category through our website or by phone. After receiving your request, our support team may contact you to discuss your enquiry and possible booking assistance options. Submitting an enquiry does not guarantee a confirmed booking, final price, vehicle availability, or provider approval.",
  },
  {
    question: "4. Can I book a rental car by phone?",
    answer:
      "Yes. You can call our support team at 1-877-991-0909 for car rental enquiry assistance. Our team can help you share your pickup location, travel dates, preferred vehicle type, and other rental request details.",
  },
  {
    question: "5. Are the prices shown on the website final?",
    answer:
      "Rates shown on the website may be estimated base rates or example starting prices for general guidance. Actual rental prices may vary based on pickup location, dates, rental duration, vehicle category, taxes, fees, demand, provider rates, driver age, insurance options, deposits, and availability. Final pricing should be reviewed before accepting or completing any rental arrangement.",
  },
  {
    question: "6. Are vehicle images exact models?",
    answer:
      "Vehicle images and category descriptions are for general illustration and comparison purposes only. The exact make, model, color, year, seating capacity, luggage capacity, and features may vary by provider, category, location, and availability. A vehicle category does not guarantee that a specific vehicle model will be provided.",
  },
  {
    question: "7. What documents may be required for pickup?",
    answer:
      "Rental providers may require a valid driver's license, acceptable payment card, proof of identity, minimum driver age, security deposit, and compliance with provider terms. Additional requirements may vary depending on the rental provider, location, vehicle type, and customer profile.",
  },
  {
    question: "8. Can I cancel or change my rental request?",
    answer:
      "Cancellation and amendment rules may depend on the rental provider, booking status, selected rate type, vehicle category, pickup location, and timing of the request. Some bookings may be refundable, partially refundable, or non-refundable depending on provider terms. USACarBooking may assist with cancellation or amendment enquiries, but we cannot guarantee approval, refund eligibility, or processing time.",
  },
  {
    question: "9. Do you guarantee vehicle availability?",
    answer:
      "No. We do not guarantee vehicle availability, exact vehicle model, final price, provider approval, deposit amount, insurance options, or final rental terms. Availability and final rental conditions may depend on the applicable rental provider and selected pickup location.",
  },
  {
    question: "10. Are you affiliated with rental car brands?",
    answer:
      "No. USACarBooking is an independent enquiry assistance service. We are not affiliated with, endorsed by, sponsored by, or officially connected to any specific car rental brand, rental supplier, airport, or travel company. Any brand names, location names, vehicle images, or service references are used for general information and descriptive purposes only.",
  },
  {
    question: "11. How can I contact customer support?",
    answer:
      "You can call us at 1-877-991-0909 or email us at reservations@USACarBooking.com. Please include your name, contact details, pickup location, travel dates, and preferred vehicle type so our support team can assist you better.",
  },
  {
    question: "12. Is my information secure?",
    answer:
      "We use standard measures to help protect information submitted through our website and support channels. You should not submit sensitive payment information, full card numbers, CVV codes, banking passwords, or private financial documents through general enquiry forms or email channels. For more details, please review our Privacy Policy.",
  },
];

const FAQ = () => {
  const handlePhoneClick = () => {
    if (window.gtag) {
      window.gtag("config", "AW-18343552397/3FAKCIr6l9ccEI3L8apE", {
        phone_conversion_number: "1-877-991-0909",
      });
    }
  };
  return (
    <main className="w-full bg-[#f4f7fb] font-sans text-slate-800">
      {/* SECTION 1: Dark Hero Section */}
      <section className="w-full bg-[#091b3e] text-white pt-16 lg:pt-24 pb-32 lg:pb-40 px-4 sm:px-6 lg:px-8 text-center relative">
        <div className="max-w-4xl mx-auto flex flex-col items-center">
          <div className="flex items-center gap-2 mb-4 text-white/80">
            <svg
              className="w-3 h-3 text-[#e32b22]"
              fill="currentColor"
              viewBox="0 0 16 16"
            >
              <path d="M10 13l-5-5 5-5v10z" />
            </svg>
            <span className="text-xs font-bold uppercase tracking-widest">
              How we can help
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-6">
            Frequently Asked <span className="text-[#e32b22]">Questions</span>
          </h1>

          <p className="text-lg sm:text-xl text-slate-300 leading-relaxed max-w-3xl mb-8">
            Find answers about our independent car rental enquiry assistance
            service, estimated rates, vehicle availability, provider terms,
            cancellations, and customer support.
          </p>

          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-2 text-sm font-medium text-white/90 backdrop-blur-sm">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            Last updated: 14 June 2024
          </div>
        </div>
      </section>

      {/* SECTION 2: Overlapping Features Bar */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-20 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-slate-100 flex flex-col items-center text-center gap-3">
            <div className="bg-slate-50 text-[#e32b22] p-3 rounded-full w-12 h-12 flex items-center justify-center mb-1">
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                />
              </svg>
            </div>
            <h3 className="font-bold text-[#091b3e] text-lg">
              Independent Assistance
            </h3>
            <p className="text-slate-500 text-sm">
              We help with car rental enquiries and are not affiliated with
              rental car brands.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 border border-slate-100 flex flex-col items-center text-center gap-3">
            <div className="bg-slate-50 text-[#e32b22] p-3 rounded-full w-12 h-12 flex items-center justify-center mb-1">
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="font-bold text-[#091b3e] text-lg">
              USA Rental Support
            </h3>
            <p className="text-slate-500 text-sm">
              Submit requests for popular USA city and airport pickup locations.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 border border-slate-100 flex flex-col items-center text-center gap-3">
            <div className="bg-slate-50 text-[#e32b22] p-3 rounded-full w-12 h-12 flex items-center justify-center mb-1">
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="font-bold text-[#091b3e] text-lg">
              Clear Information
            </h3>
            <p className="text-slate-500 text-sm">
              Final rates, availability, deposits, and terms may vary by
              provider.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 3: Main FAQ Content Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          {/* Left Column (Sticky Sidebar on Desktop) */}
          <div className="lg:col-span-4 lg:sticky lg:top-8 flex flex-col gap-6">
            {/* Summary Box */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-4 border-b border-slate-100 pb-4">
                <div className="bg-[#e32b22] text-white p-2 rounded-lg flex-shrink-0">
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                    />
                  </svg>
                </div>
                <h3 className="text-[#091b3e] font-bold text-lg">
                  FAQ Summary
                </h3>
              </div>
              <p className="text-slate-600 text-sm leading-relaxed mb-4">
                We help you understand how to request rental vehicle
                information. We act as an independent service and are not
                affiliated with any rental car brand.
              </p>
              <p className="text-slate-600 text-sm leading-relaxed">
                Final vehicle availability, pricing, deposits, and terms are
                determined by the applicable supplier.
              </p>
            </div>

            {/* Need Help Box */}
            <div className="bg-[#091b3e] rounded-2xl shadow-sm p-6 sm:p-8 text-white">
              <h3 className="font-bold text-lg mb-4 text-[#e32b22] uppercase tracking-wide text-sm">
                Need Help?
              </h3>
              <div className="space-y-4">
                <a
                  href="tel:+18779910909"
                  onClick={handlePhoneClick}
                  className="flex items-center gap-3 hover:text-slate-300 transition-colors"
                >
                  <svg
                    className="w-5 h-5 text-white/70"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                  <span className="font-bold text-sm">1-877-991-0909</span>
                </a>
                <a
                  href="mailto:reservations@USACarBooking.com"
                  className="flex items-center gap-3 hover:text-slate-300 transition-colors break-words"
                >
                  <svg
                    className="w-5 h-5 text-white/70"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    strokeWidth="2"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  <span className="font-bold text-sm truncate">
                    reservations@USACarBooking.com
                  </span>
                </a>
              </div>
            </div>
          </div>

          {/* Right Column (The FAQs) */}
          <div className="lg:col-span-8 flex flex-col gap-4">
            {/* Disclaimer Banner */}
            <div className="bg-white border border-[#e32b22]/20 rounded-xl p-4 flex items-start gap-3 mb-2 shadow-sm">
              <svg
                className="w-5 h-5 text-[#e32b22] flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <p className="text-slate-600 text-sm leading-relaxed">
                These FAQs apply to USACarBooking website enquiries, phone
                support, email support, and web forms interaction, and not real
                customer counters channels.
              </p>
            </div>

            {/* Interactive Accordion Map */}
            {faqData.map((faq, index) => (
              <details
                key={index}
                className="group bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden open:border-[#091b3e]/30 transition-colors"
              >
                <summary className="flex items-center justify-between cursor-pointer p-5 sm:p-6 font-bold text-[#091b3e] text-base sm:text-lg select-none hover:bg-slate-50">
                  <span className="pr-4">{faq.question}</span>
                  <span className="flex-shrink-0 text-[#e32b22] transition duration-300 group-open:-rotate-180">
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      strokeWidth="2"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </span>
                </summary>
                <div className="p-5 sm:p-6 pt-0 sm:pt-0 text-slate-600 text-sm sm:text-base leading-relaxed border-t border-slate-100 mt-2">
                  {faq.answer}
                </div>
              </details>
            ))}

            {/* Important Notice Alert at Bottom */}
            <div className="bg-red-50 border border-red-100 rounded-2xl p-6 mt-6 flex items-start gap-4">
              <svg
                className="w-6 h-6 text-[#e32b22] flex-shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <div>
                <h4 className="font-bold text-[#091b3e] mb-1">
                  Important service notice
                </h4>
                <p className="text-slate-600 text-sm leading-relaxed">
                  USACarBooking is an independent enquiry assistance website.
                  Submitting a request does not guarantee vehicle availability,
                  exact pricing, provider approval, deposits, insurance options,
                  or final rental terms.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default FAQ;
